import { WARDFlexCalculator } from '../../components/wardflexCalculator.js';

const calculator = new WARDFlexCalculator();

export default {
  namespaced: true,

  state: {
    // Input parameters
    gasType: null, // 'NaturalGas' or 'Propane'
    gasPressure: 0,
    gasPressureUnit: 'psi', // 'psi' or 'inchesWC'
    pressureDrop: 0,
    pressureDropUnit: 'psi', // 'psi' or 'inchesWC'
    minLength: 10,
    maxLength: 100,
    lengthIncrements: 10,

    // Results and state
    resultData: null,
    error: null,
    isCalculating: false,

    // Options for dropdowns
    gasTypeOptions: [
      { value: 'NaturalGas', label: 'Natural Gas' },
      { value: 'Propane', label: 'Propane (LPG)' }
    ],

    pressureUnitOptions: [
      { value: 'psi', label: 'PSI' },
      { value: 'inchesWC', label: 'Inches W.C.' }
    ]
  },

  getters: {
    allFieldsFilled(state) {
      return state.gasType !== null &&
             state.gasType !== '' &&
             state.gasPressure > 0 &&
             state.pressureDrop > 0 &&
             state.minLength > 0 &&
             state.maxLength > 0 &&
             state.lengthIncrements > 0;
    },

    hasResults(state) {
      return state.resultData !== null;
    },

    calculationParams(state) {
      return {
        gasType: state.gasType,
        gasPressure: state.gasPressure,
        gasPressureUnit: state.gasPressureUnit,
        pressureDrop: state.pressureDrop,
        pressureDropUnit: state.pressureDropUnit,
        minLength: state.minLength,
        maxLength: state.maxLength,
        lengthIncrements: state.lengthIncrements
      };
    },

    // Get units for display based on gas type
    capacityUnits(state) {
      return state.gasType === 'NaturalGas' ? 'CFH' : 'KBTU';
    },

    // Get pressure validation info
    pressureValidation(state) {
      const gasPressurePsi = state.gasPressureUnit === 'inchesWC'
        ? state.gasPressure / 27.73
        : state.gasPressure;
      const pressureDropPsi = state.pressureDropUnit === 'inchesWC'
        ? state.pressureDrop / 27.73
        : state.pressureDrop;

      return {
        gasPressurePsi,
        pressureDropPsi,
        isValidSupplyPressure: gasPressurePsi <= 5,
        isValidPressureDrop: gasPressurePsi >= pressureDropPsi,
        isValidLength: state.minLength <= state.maxLength && state.maxLength <= 2000
      };
    }
  },

  mutations: {
    setGasType(state, gasType) {
      state.gasType = gasType;
      // Clear results when changing parameters
      state.resultData = null;
      state.error = null;
    },

    setGasPressure(state, gasPressure) {
      state.gasPressure = gasPressure;
      state.resultData = null;
      state.error = null;
    },

    setGasPressureUnit(state, unit) {
      state.gasPressureUnit = unit;
      state.resultData = null;
      state.error = null;
    },

    setPressureDrop(state, pressureDrop) {
      state.pressureDrop = pressureDrop;
      state.resultData = null;
      state.error = null;
    },

    setPressureDropUnit(state, unit) {
      state.pressureDropUnit = unit;
      state.resultData = null;
      state.error = null;
    },

    setMinLength(state, minLength) {
      state.minLength = minLength;
      state.resultData = null;
      state.error = null;
    },

    setMaxLength(state, maxLength) {
      state.maxLength = maxLength;
      state.resultData = null;
      state.error = null;
    },

    setLengthIncrements(state, lengthIncrements) {
      state.lengthIncrements = lengthIncrements;
      state.resultData = null;
      state.error = null;
    },

    setResultData(state, resultData) {
      state.resultData = resultData;
      state.error = null;
    },

    setError(state, error) {
      state.error = error;
      state.resultData = null;
    },

    setIsCalculating(state, isCalculating) {
      state.isCalculating = isCalculating;
    },

    clearResults(state) {
      state.resultData = null;
      state.error = null;
    },

    resetForm(state) {
      state.gasType = null;
      state.gasPressure = 0;
      state.gasPressureUnit = 'psi';
      state.pressureDrop = 0;
      state.pressureDropUnit = 'psi';
      state.minLength = 10;
      state.maxLength = 100;
      state.lengthIncrements = 10;
      state.resultData = null;
      state.error = null;
      state.isCalculating = false;
    }
  },

  actions: {
    async calculateTable({ commit, getters, state }) {
      // Validate all fields are filled
      if (!getters.allFieldsFilled) {
        commit('setError', 'Please fill in all required fields');
        return;
      }

      commit('setIsCalculating', true);
      commit('setError', null);

      try {
        const params = getters.calculationParams;

        // Use the calculator to generate the sizing table
        const result = calculator.calculateSizingTable(params);

        if (result.success) {
          commit('setResultData', result);

          // Track calculation event for analytics if available
          if (window.gtag) {
            window.gtag('event', 'wardflex_calculation', {
              'gas_type': params.gasType,
              'supply_pressure': params.gasPressure,
              'pressure_unit': params.gasPressureUnit,
              'pressure_drop': params.pressureDrop,
              'drop_unit': params.pressureDropUnit,
              'length_range': `${params.minLength}-${params.maxLength}`,
              'increments': params.lengthIncrements
            });
          }
        } else {
          commit('setError', result.error);
        }
      } catch (error) {
        console.error('WARDFlex calculation error:', error);
        commit('setError', 'An error occurred during calculation. Please check your inputs and try again.');
      } finally {
        commit('setIsCalculating', false);
      }
    },

    // Action to load preset configurations for testing
    loadPresetConfiguration({ commit }, presetName) {
      const presets = {
        'naturalGas_halfPsi': {
          gasType: 'NaturalGas',
          gasPressure: 0.5,
          gasPressureUnit: 'psi',
          pressureDrop: 0.5,
          pressureDropUnit: 'inchesWC',
          minLength: 10,
          maxLength: 100,
          lengthIncrements: 10
        },
        'propane_2psi': {
          gasType: 'Propane',
          gasPressure: 2,
          gasPressureUnit: 'psi',
          pressureDrop: 1.5,
          pressureDropUnit: 'inchesWC',
          minLength: 25,
          maxLength: 200,
          lengthIncrements: 25
        },
        'naturalGas_5psi': {
          gasType: 'NaturalGas',
          gasPressure: 5,
          gasPressureUnit: 'psi',
          pressureDrop: 3.5,
          pressureDropUnit: 'inchesWC',
          minLength: 50,
          maxLength: 500,
          lengthIncrements: 50
        }
      };

      const preset = presets[presetName];
      if (preset) {
        commit('setGasType', preset.gasType);
        commit('setGasPressure', preset.gasPressure);
        commit('setGasPressureUnit', preset.gasPressureUnit);
        commit('setPressureDrop', preset.pressureDrop);
        commit('setPressureDropUnit', preset.pressureDropUnit);
        commit('setMinLength', preset.minLength);
        commit('setMaxLength', preset.maxLength);
        commit('setLengthIncrements', preset.lengthIncrements);
      }
    },

    // Export calculation data
    exportCalculationData({ state }) {
      if (!state.resultData) {
        return null;
      }

      return {
        timestamp: new Date().toISOString(),
        parameters: {
          gasType: state.gasType,
          gasPressure: state.gasPressure,
          gasPressureUnit: state.gasPressureUnit,
          pressureDrop: state.pressureDrop,
          pressureDropUnit: state.pressureDropUnit,
          minLength: state.minLength,
          maxLength: state.maxLength,
          lengthIncrements: state.lengthIncrements
        },
        results: state.resultData
      };
    }
  }
};
